import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, Play, Pause, Activity, Zap, Square, ChevronDown, ChevronUp } from 'lucide-react';

const WorkflowProgress = ({ 
  isRunning, 
  progress, 
  currentNode, 
  totalNodes, 
  completedNodes, 
  failedNodes, 
  estimatedTime,
  elapsedTime,
  nodeStatuses,
  onPauseResume,
  onStop 
}) => {
  const [displayTime, setDisplayTime] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setDisplayTime(prev => prev + 1);
      }, 1000);
    } else {
      setDisplayTime(elapsedTime || 0);
    }
    return () => clearInterval(interval);
  }, [isRunning, elapsedTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressColor = () => {
    if (failedNodes > 0) return 'bg-red-500';
    if (isRunning) return 'bg-blue-500';
    if (progress === 100) return 'bg-green-500';
    return 'bg-gray-300';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <Clock className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  if (!isRunning && progress === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden backdrop-blur-sm">
      {/* Gradient Header */}
      <div className={`px-6 py-4 bg-gradient-to-r ${
        isRunning ? 'from-blue-500 to-purple-600' : 
        progress === 100 ? 'from-green-500 to-emerald-600' : 
        'from-gray-500 to-gray-600'
      } text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              {isRunning ? (
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Activity className="w-4 h-4 animate-pulse" />
                </div>
              ) : progress === 100 ? (
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4" />
                </div>
              ) : (
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Square className="w-4 h-4" />
                </div>
              )}
              {isRunning && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg leading-tight">
                {isRunning ? 'Workflow Running' : progress === 100 ? 'Workflow Complete' : 'Workflow Stopped'}
              </h3>
              <p className="text-white/80 text-sm">
                {isRunning ? `Processing ${currentNode || 'workflow'}...` : 
                 progress === 100 ? 'All tasks completed successfully' : 
                 'Execution stopped'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isRunning && (
              <>
                <button
                  onClick={onPauseResume}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 hover:scale-110"
                  title="Pause workflow"
                >
                  <Pause className="w-4 h-4" />
                </button>
                <button
                  onClick={onStop}
                  className="p-2 bg-white/20 hover:bg-red-500 rounded-lg transition-all duration-200 hover:scale-110"
                  title="Stop workflow"
                >
                  <Square className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200"
              title={showDetails ? "Hide details" : "Show details"}
            >
              {showDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="text-2xl font-bold text-gray-900">{Math.round(progress)}%</div>
            <div className="text-sm text-gray-600">
              <div>{completedNodes} of {totalNodes} tasks</div>
              <div className="text-xs text-gray-500">{formatTime(displayTime)} elapsed</div>
            </div>
          </div>
          {estimatedTime && (
            <div className="text-right text-sm text-gray-500">
              <div className="text-xs">Estimated</div>
              <div className="font-medium">
                {typeof estimatedTime === 'string' ? estimatedTime : 
                 (estimatedTime.formatted || `${estimatedTime.minutes}:${estimatedTime.seconds?.toString().padStart(2, '0') || '00'}`)}
              </div>
            </div>
          )}
        </div>
        
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-700 ease-out ${
                failedNodes > 0 ? 'bg-gradient-to-r from-red-400 to-red-500' :
                isRunning ? 'bg-gradient-to-r from-blue-400 to-purple-500' :
                progress === 100 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                'bg-gray-400'
              }`}
              style={{ width: `${progress}%` }}
            >
              {isRunning && (
                <div className="h-full bg-gradient-to-r from-white/30 to-transparent animate-pulse"></div>
              )}
            </div>
          </div>
          {isRunning && (
            <div className="absolute -top-1 -bottom-1 bg-blue-400 w-1 rounded-full animate-pulse" 
                 style={{ left: `${progress}%`, transform: 'translateX(-50%)' }}>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-6 pb-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
            <div className="text-lg font-semibold text-green-600">{completedNodes}</div>
            <div className="text-xs text-green-600">Completed</div>
          </div>
          
          {failedNodes > 0 && (
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="flex items-center justify-center mb-1">
                <XCircle className="w-4 h-4 text-red-500" />
              </div>
              <div className="text-lg font-semibold text-red-600">{failedNodes}</div>
              <div className="text-xs text-red-600">Failed</div>
            </div>
          )}
          
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <Clock className="w-4 h-4 text-gray-500" />
            </div>
            <div className="text-lg font-semibold text-gray-600">{totalNodes - completedNodes - failedNodes}</div>
            <div className="text-xs text-gray-600">Pending</div>
          </div>
        </div>
        
        {isRunning && (
          <div className="mt-4 flex items-center justify-center space-x-2 p-3 bg-blue-50 rounded-lg">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <span className="text-blue-600 font-medium text-sm">Processing workflow...</span>
          </div>
        )}
      </div>

      {/* Detailed Status (Collapsible) */}
      {showDetails && (failedNodes > 0 || Object.keys(nodeStatuses).length > 0) && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="px-6 py-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <Activity className="w-4 h-4 mr-2" />
              Task Details
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {Object.entries(nodeStatuses).map(([nodeId, status]) => (
                <div key={nodeId} className="flex items-center justify-between p-2 bg-white rounded-lg shadow-sm">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(status.state)}
                    <span className={`text-sm font-medium truncate ${
                      status.state === 'failed' ? 'text-red-600' : 
                      status.state === 'completed' ? 'text-green-600' : 
                      status.state === 'running' ? 'text-blue-600' : 'text-gray-600'
                    }`}>
                      {status.label || nodeId}
                    </span>
                  </div>
                  {status.state === 'failed' && status.error && (
                    <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded" title={status.error}>
                      Error
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowProgress;